import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AnalyticsService } from '@core/analytics.service';
import { AppRoutingService } from '@core/app-routing.service';
import { AppConfigService } from '@core/appconfig.service';
import { AuthenticationService } from '@core/authentication.service';
import { BrowserService } from '@core/browser.service';
import { CacheService } from '@core/cache.service';
import { UserService } from '@core/user.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { brandPortalNames } from '@shared/enums/brand-portal-names';
import { LoginPageRolesByRoute } from '@shared/enums/login-page-type';
import { FormHelpers } from '@shared/form-helpers';
import {
  GenericConfirmModalComponent,
} from '@shared/modals/generic-confirm-modal/generic-confirm-modal.component';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  of,
  shareReplay,
  skipWhile,
  Subscription,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { LoginFormModel } from './login-form-model';

@Component({
  selector: 'zbp-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  standalone: false
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() isQrScannerRunning$: BehaviorSubject<boolean>;
  errorMessage: string = '';
  showPassword = false;
  isHighlights: boolean = false;
  brandClass: string = '';
  email = '';
  password = '';
  success = null;
  subTitle = '';
  loginProcessing = false;
  loginFormMode: LoginPageRolesByRoute;
  isLoading: boolean = false;
  welcomeText: string = 'Welcome!';
  subtitleText: string = '';
  currentModeContent: LoginFormModel = null;
  schoolId: string;
  studentPageRole = LoginPageRolesByRoute.Student;
  teacherAndAdminLoginText = 'Teacher and Administrator Login';

  private modalRef: NgbModalRef;
  private subscriptions: Subscription[] = [];
  private teacherAndAdminRoute = '/login/user';
  private parentRoute = '/login/parent';
  private studentRoute = '/login/student';
  private brand: brandPortalNames = brandPortalNames.zb;

  loginRoleTypes: LoginFormModel[] = [
    {
      id: 'admin-teacher',
      mode: LoginPageRolesByRoute.User,
      subtitle: 'Teachers and administrators',
      route: this.teacherAndAdminRoute,
      zbpIconName: zbpIconNames.admin_teacher_role,
      userNameFieldName: 'Email',
      weight: 1,
      currentPage: this.determineCurrentPageState(this.teacherAndAdminRoute)
    },
    {
      id: 'student',
      mode: LoginPageRolesByRoute.Student,
      subtitle: 'Students',
      route: this.studentRoute,
      zbpIconName: zbpIconNames.student_role,
      userNameFieldName: 'Username',
      weight: 2,
      currentPage: this.determineCurrentPageState(this.studentRoute)
    },
    {
      id: 'parent',
      mode: LoginPageRolesByRoute.Parent,
      subtitle: 'Parents and caregivers',
      route: this.parentRoute,
      zbpIconName: zbpIconNames.parent_role,
      userNameFieldName: 'Email',
      weight: 0,
      currentPage: this.determineCurrentPageState(this.parentRoute)
    },
  ];

  getContentFromButtonForFormMode(formMode: LoginPageRolesByRoute) {
    return this.loginRoleTypes.find(button => button.mode === formMode?.toString()?.toLowerCase());
  }

  /**
   * Login form.
   *
   * This is instantiated here so that statusChanges observable can be used in a component property.
   */
  loginForm: FormGroup = this.formBuilder.group({
    userName: this.formBuilder.control('', [Validators.required, FormHelpers.getStrictEmailValidator()]),
    password: this.formBuilder.control('', [Validators.required]),
    schoolId: this.formBuilder.control(''),
  });

  /**
   * Maps authorization status to local observable.
   */
  hasLoggedIn$: Observable<boolean> = this.authService.authStatus.pipe(
    shareReplay(),
  );

  /**
   * TODO ZBP-13633 this logic is duplicated elsewhere
   * Redirects the user if they're logged in.
   */
  redirectOnLoggedIn$: Observable<boolean> = this.hasLoggedIn$.pipe(
    withLatestFrom(this.userService.user$),
    switchMap(([loggedIn, user]) => {
      if (loggedIn) {
        const redirectPath = (user.roles.length > 1 || !user.isTeacher)
          ? ''
          : this.appRoutingService.getECEWeeklyPlannerRoute().toString();
        return this.router.navigate([redirectPath]);
      }
      return Promise.resolve(false);
    }),
  );

  /**
   * Checks the userName control validity.
   */
  userNameError$: Observable<boolean> = this.loginForm.get('userName').statusChanges.pipe(
    map(status => status === 'INVALID'),
  );

  /**
   * Checks the password control validity.
   */
  passwordError$: Observable<boolean> = this.loginForm.get('password').statusChanges.pipe(
    map(status => status === 'INVALID'),
  );

  /**
   * Checks the school ID control validity, if it exists on this form.
   */
  schoolIdError$: Observable<boolean> = this.loginForm.get('schoolId').statusChanges.pipe(
    map(status => status === 'INVALID'),
  );

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private userService: UserService,
    private appConfig: AppConfigService,
    private titleService: Title,
    private cache: CacheService,
    public browserService: BrowserService,
    private analyticsService: AnalyticsService,
    private modalService: NgbModal,
    private appRoutingService: AppRoutingService,
  ) {
  }

  ngOnInit(): void {
    this.brand = this.appConfig.getBrandTitle;

    this.subscriptions.push(
      this.route.data.pipe(
        skipWhile((params: Params) => !params),
        map((params: Params) => {
          this.loginFormMode = params.formMode;
          this.currentModeContent = this.getContentFromButtonForFormMode(this.loginFormMode);
          this.subtitleText = this.currentModeContent.subtitle;

          return params;
        }),
      ).subscribe(() => {
        this.isHighlights = this.appConfig.isHighlightsPortalUrl;
        if (this.isHighlights) {
          this.redirectOnLoggedIn$.subscribe();
          this.setupHighlightsPortalData();
        } else {
          this.setupZBPortalData();
        }
        this.isLoading = false;
      }),
    );

    if (this.cache.lastSchoolIdUsed) {
      this.schoolId = this.cache.lastSchoolIdUsed;
    }

    if (!this.isQrScannerRunning$) {
      this.isQrScannerRunning$ = new BehaviorSubject<boolean>(false);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(sub => sub.unsubscribe());
  }

  private determineCurrentPageState(route: string) {
    return this.router.url === route ? 'current page' : '';
  }

  /**
   * Sets up the data for ZB Portal
   * Component defaults to ZB Portal data, but still needs manipulated based on route/form mode.
   * Changes display order of Role icons based on route (form mode)
   * Applies title to browser tab for ZB Brand & Role
   */
  private setupZBPortalData() {
    this.brandClass = 'zb-portal-login-form';

    if (this.cache.lastEmailUsed) {
      this.email = this.cache.lastEmailUsed;
    }

    const loginFormModeLowerCase = this.loginFormMode?.toString()?.toLowerCase();

    if (loginFormModeLowerCase === LoginPageRolesByRoute.Parent?.toString()?.toLowerCase()) {
      this.setIconWeightForMode(0, 1, 2);
      this.titleService.setTitle(`Parent and Caregiver Login | ${this.brand}`);
    } else if (loginFormModeLowerCase === LoginPageRolesByRoute.User?.toString()?.toLowerCase()) {
      this.setIconWeightForMode(1, 0, 2);
      this.titleService.setTitle(`${this.teacherAndAdminLoginText} | ${this.brand}`);
    } else if (loginFormModeLowerCase === LoginPageRolesByRoute.Student?.toString()?.toLowerCase()) {
      this.setIconWeightForMode(0, 2, 1);
      this.brandClass = 'zb-portal-login-form zb-portal-student-login-form';
      this.titleService.setTitle(`Student Login | ${this.brand}`);
      this.loginForm.get('userName').clearValidators();
      this.loginForm.get('userName').setValidators(Validators.required);
      this.loginForm.get('schoolId').clearValidators();
      this.loginForm.get('schoolId').setValidators(Validators.required);
      this.loginForm.updateValueAndValidity({ onlySelf: true });
      if (this.cache.lastSchoolIdUsed) {
        const schoolId = this.cache.lastSchoolIdUsed || '';
        this.loginForm.patchValue({ schoolId });
      }
    }

    this.loginRoleTypes = this.loginRoleTypes.sort((a, b) => a.weight - b.weight);
  }

  /*
  * Applies weight to icons.
  * Used to determine the order that the Login Role icons display in based on the route
  * Active icon should be in the middle
  */
  private setIconWeightForMode(userWeight: number, parentsWeight: number, studentsWeight: number) {
    this.loginRoleTypes.forEach((type) => {
      switch (type.mode?.toLowerCase()) {
      case LoginPageRolesByRoute?.User:
        type.weight = userWeight;
        return type;
      case LoginPageRolesByRoute?.Student:
        type.weight = studentsWeight;
        return type;
      case LoginPageRolesByRoute?.Parent:
        type.weight = parentsWeight;
        return type;
      default:
        type.weight = userWeight;
        return type;
      }
    });
  }

  /**
   * Sets up the data for Highlights Portal
   * Applies title to browser tab for Highlights
   * HL Portal currently only supports the User mode (Admins and Teachers)
   */
  private setupHighlightsPortalData() {
    this.titleService.setTitle(`${this.teacherAndAdminLoginText} | | ${this.brand}`);
    this.welcomeText = 'Welcome';
    this.subtitleText = 'early childhood educators';
  }

  /**
   * Toggles whether to show password or not.
   */
  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  private checkAndShowRequiredErrors() {
    // show field required errors when form is submitted & not touched
    if (!this.loginForm.valid) {
      const emailControlHasErrors = this.loginForm.get('userName')?.errors?.['required'];
      const passwordControlHasErrors = this.loginForm.get('password')?.errors?.['required'];
      const schoolIdControlHasErrors = this.loginForm.get('schoolId')?.errors?.['required'];

      if (emailControlHasErrors) {
        // show Email required error
        this.userNameError$ = of(true);
      }

      if (passwordControlHasErrors) {
        // show Password required error
        this.passwordError$ = of(true);
      }

      if (schoolIdControlHasErrors) {
        this.schoolIdError$ = of(true);
      }
    }
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.success = null;
    this.passwordError$ = of(false);
    this.userNameError$ = of(false);
    this.schoolIdError$ = of(false);
  }

  /**
   * Handles login form submission for both brands.
   * Form validation is handled inline in the template per specification.
   */
  login() {
    this.clearMessages();
    if (!this.loginForm.valid) {
      this.checkAndShowRequiredErrors();
    } else if (this.loginForm.valid) {
      this.loginProcessing = true;
      this.errorMessage = null;
      const { userName, password, schoolId } = this.loginForm.value;

      const username = this.loginFormMode === LoginPageRolesByRoute.Student
        ? `${userName}_${schoolId}`.toLocaleLowerCase()
        : userName;

      this.authService.login(username, password)
        .subscribe((res) => {
          if (res?.success) {
            this.setLastLoginUsed(username, schoolId);

            // Unique to ZB Portal
            if (!this.isHighlights) {
              this.analyticsService.setUserProperties();
              this.router.navigateByUrl('');
            }
          } else if (res?.messages) {
            const [error] = res.messages;
            this.errorMessage = error;
            this.loginProcessing = false;
          } else {
            this.errorMessage = 'An unknown application error occurred.';
            this.loginProcessing = false;
          }
        });
    } else {
      this.loginProcessing = false;
    }
  }

  /**
   * Handles clicking on forgot password button.
   *
   * All users except students will be routed to the forgot password page. Students are provided with a modal
   * confirmation for instructions.
   */
  handleForgotPassword(): void {
    if (this.loginFormMode === LoginPageRolesByRoute.Student) {
      this.modalRef = GenericConfirmModalComponent.open(this.modalService, {
        title: 'Forgot your password?',
        cancel: { enabled: false },
        confirm: { label: 'OK' },
        options: { size: 'md' },
        body: `If you can't remember your password, please ask your teacher to reset it for you.`,
      });

      from(this.modalRef.result)
        .pipe(
          catchError(() => of(null))
        )
        .subscribe();
    } else {
      this.router.navigate(['../..', 'login', 'forgot-password']);
    }
  }

  private setLastLoginUsed(username: string, schoolId?: string): void {
    if (this.loginFormMode === LoginPageRolesByRoute.Parent) {
      this.cache.lastEmailUsed = username;
      this.cache.setParentAsLastLoginPathUsed();
    } else if (this.loginFormMode === LoginPageRolesByRoute.Student) {
      this.cache.lastSchoolIdUsed = schoolId;
      this.cache.setStudentAsLastLoginPathUsed();
      // @todo SKOF login
    } else {
      this.cache.lastEmailUsed = username;
      this.cache.setTeacherAsLastLoginPathUsed();
    }
  }
}
